import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { bool, func } from 'prop-types'
import { Link } from 'gatsby'
import { useLocation } from '@reach/router'

import * as spacing from 'styles/spacing'
import { white, black } from 'styles/colors'
import { media, mediaDown } from 'styles/media'
import { navHeight, navHeightMobile, animationTime, animationCurve } from 'styles/global'

import Cart from './Cart'
import MobileNav from 'components/MobileNav/index'
import AccountDropdown from './AccountDropdown'
import MegaMenu from './MegaMenu/index'
import ProvinceSelector from 'components/Nav/ProvinceSelector'

import Logo from 'images/logo.svg'
import Mark from 'images/logo-mark.svg'

export const Container = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${spacing.large};
  color: ${props => (props.transparent ? white : black)};
  height: ${navHeightMobile};

  ${media.medium`
    height: ${navHeight};
    padding: 0 ${spacing.xLarge};
  `};
`

export const DesktopLogo = styled(Logo)`
  transition: fill ${animationTime} ${animationCurve};
  fill: ${props => (props.transparent ? white : black)};
  width: 114px;
  height: 34px;

  ${media.xLarge`
    width: 124px;
    height: 37px;
  `};

  ${mediaDown.medium`
    display: none;
  `};
`

export const MobileLogo = styled(Mark)`
  transition: fill ${animationTime} ${animationCurve};
  fill: ${props => (props.transparent ? white : black)};
  width: 32px;
  height: 32px;

  ${media.medium`
    display: none;
  `};
`

export const LogoWrap = styled(props => <Link {...props} />)`
  display: flex;
  flex-shrink: 0;
  cursor: pointer;
`

const List = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  padding-left: 48px;

  ${media.large`
    padding-left: 64px;
  `};

  ${media.xLarge`
    padding-left: 80px;
  `};

  ${mediaDown.medium`
    display: none;
  `};
`

const ProvinceSelectorContainer = styled.div`
  ${mediaDown.medium`
    display: none;
  `};
`

const Nav = ({ transparent, hoverState, hoverFunc, hideLogin }) => {
  const [hoveredItem, setHoveredItem] = useState(null)
  const hoveredRef = useRef(null)
  const timeout = useRef(null)
  const location = useLocation()

  const handleHover = itemId => {
    if (itemId && !hoveredItem) {
      // When menu is currently and item is first hovered
      setHoveredItem(itemId)
    } else if (itemId === null) {
      // When leaving menu
      if (hoveredItem) {
        timeout.current = setTimeout(() => {
          if (hoveredRef.current === itemId) setHoveredItem(null)
        }, 250)
      }
    } else {
      // When switching between menu items
      clearTimeout(timeout.current)
      timeout.current = setTimeout(() => {
        if (hoveredRef.current === itemId) setHoveredItem(itemId)
      }, 80)
    }

    hoveredRef.current = itemId
  }

  useEffect(() => {
    hoverFunc(!!hoveredItem)
  }, [hoveredItem])

  return (
    <Container transparent={transparent}>
      <MobileNav hideLogin={hideLogin} />
      <LogoWrap to="/" data-tracking="nav--link--image--Vype_Home">
        <DesktopLogo transparent={transparent ? 'true' : undefined} />
        <MobileLogo transparent={transparent ? 'true' : undefined} />
      </LogoWrap>
      <List>
        <MegaMenu
          transparent={transparent}
          title="Shop"
          items={[
            {
              title: 'ePod 2',
              figure: 'epod',
              figureWidth: 13,
              weight: 0.8,
              links: [
                {
                  label: 'Device',
                  link: '/vuse-epod-2/'
                },
                {
                  label: 'Pods',
                  link: '/buy-online/vype-epod-vpro-cartridges/'
                },
                {
                  label: 'Accessories',
                  link: '/buy-online/vype-epod-accessories/'
                }
              ]
            },
            {
              title: 'Personalize your ePod',
              figure: 'vuse-x-you',
              figureWidth: 100,
              weight: 1,
              links: [
                {
                  label: 'Experience VUSE x U',
                  link: `${location.origin}${process.env.BASE_PATH}/vusebyu/`
                },
                {
                  label: 'Shop ePod 2 Skins',

                  link: `${location.origin}${process.env.BASE_PATH}/vusebyu/skins/`
                },
                {
                  label: 'Engrave Your ePod 2',
                  link: `${location.origin}${process.env.BASE_PATH}/vusebyu/engravings/`
                }
              ]
            },
            {
              title: 'New Launches',
              weight: 1,
              links: [
                {
                  label: 'ePod 2',
                  link: '/vuse-epod-2/'
                },
                {
                  label: 'Skins',
                  link: '/buy-online/vype-epod-skins/'
                },
                {
                  label: 'Engraving',
                  link: `${location.origin}${process.env.BASE_PATH}/vusebyu/engravings/`
                },
                {
                  label: 'Pod Caps',
                  link: '/buy-online/pod-caps/'
                }
              ]
            }
          ]}
          hoverFunc={handleHover}
          hoverState={hoveredItem === 'Shop' && hoverState}
        />
        <MegaMenu
          transparent={transparent}
          title="Save"
          items={[
            {
              title: 'Offers',
              links: [
                {
                  label: 'Starter Bundle for $14.99',
                  link: '/buy-online/epod-starter-bundle/'
                },
                {
                  label: '3 for $33 ePod Pods',
                  link: '/buy-online/multipack-3/'
                },
                {
                  label: '5 for $50 ePod Pods',
                  link: '/buy-online/multipack-5/'
                }
              ]
            },
            {
              title: 'Programs',
              links: [
                {
                  label: 'Subscribe & Save (up to 30%)',
                  link: '/subscribe/'
                },
                {
                  label: 'Pass On The Savings (up to $200)',
                  link: '/pass-on-the-savings/'
                },
                {
                  label: 'Vuse+ Rewards',
                  link: '/vuse-plus-vape-deals/'
                }
              ]
            }
          ]}
          hoverFunc={handleHover}
          hoverState={hoveredItem === 'Save' && hoverState}
        />
        <MegaMenu
          transparent={transparent}
          title="About"
          items={[
            {
              title: 'About',
              links: [
                {
                  label: 'The Vuse Store',
                  link: '/flagship-store-toronto/'
                }
              ]
            }
          ]}
          hoverFunc={handleHover}
          hoverState={hoveredItem === 'About' && hoverState}
        />
        <MegaMenu
          transparent={transparent}
          title="Help"
          items={[
            {
              title: 'Help',
              links: [
                {
                  label: 'Shipping & Delivery',
                  link: '/shipping/'
                },
                {
                  label: 'Contact Us',
                  link: 'https://batsupport.zendesk.com/hc/en-us'
                },
                {
                  label: 'FAQ',
                  link: 'https://batsupport.zendesk.com/hc/en-us'
                },
                {
                  label: 'Store Locator',
                  link: '/vape-stores/'
                }
              ]
            }
          ]}
          hoverFunc={handleHover}
          hoverState={hoveredItem === 'Help' && hoverState}
        />
      </List>
      <ProvinceSelectorContainer>
        <ProvinceSelector transparent={transparent} noMargin={hideLogin} />
      </ProvinceSelectorContainer>
      {!hideLogin && <AccountDropdown transparent={transparent} />}

      <div>
        <Cart transparent={transparent} />
      </div>
    </Container>
  )
}

Nav.propTypes = {
  transparent: bool,
  hoverFunc: func.isRequired,
  hoverState: bool,
  hideLogin: bool
}

export default Nav
